/** Deterministic hash to replace Math.random() in score calculations. */
function simpleHash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h);
}

/**
 * Calculates initial job match score based on resume content and job requirements
 * @param originalText Original resume text
 * @param jobData Job data to match against
 * @returns Initial score (0-100)
 */
export function calculateInitialJobMatchScore(resumeText: string, jobData: any): number {
  // Use the existing job match score calculation for initial score
  return calculateJobMatchScore(resumeText, jobData.skills || [], jobData.description || '');
}

/**
 * Calculates initial score based on resume content (legacy function for backward compatibility)
 * @param text Resume text to process
 * @returns Initial score (0-100)
 */
export function calculateInitialScore(text: string): number {
  const wordCount = text.split(/\s+/).length;
  
  // Base score calculation
  let score = Math.min(100, Math.floor(wordCount / 10)); // Example: 1000 words = 100 score
  
  // Adjust based on content quality indicators
  if (/(achieved|improved|increased)/gi.test(text)) {
    score += 5; // Bonus for action verbs
  }
  
  if (/\d+%/g.test(text)) {
    score += 5; // Bonus for metrics
  }
  
  // Deterministic variation based on content hash instead of Math.random()
  const contentHash = simpleHash(text);
  const contentVariation = (contentHash % 15) - 7;
  const adjustedScore = score + contentVariation;
  
  // Ensure minimum score with more realistic range
  const finalScore = Math.max(45, Math.min(85, adjustedScore));
  
  // Validate the result
  if (isNaN(finalScore)) {
    return 65;
  }
  
  return finalScore;
}

/**
 * Calculates optimized job match score based on initial job match score and improvements
 * @param initialJobMatchScore Initial job match score
 * @param originalText Original resume text
 * @param optimizedText Optimized resume text
 * @param jobData Job data to match against
 * @returns Optimized score (0-100)
 */
export function calculateOptimizedJobMatchScore(
  initialJobMatchScore: number, 
  originalText: string, 
  optimizedText: string,
  jobData: any
): number {
  // Validate inputs
  if (typeof initialJobMatchScore !== 'number' || isNaN(initialJobMatchScore)) {
    console.warn('calculateOptimizedJobMatchScore: Invalid initialJobMatchScore, using default');
    initialJobMatchScore = 65;
  }
  
  if (!originalText || typeof originalText !== 'string') {
    console.warn('calculateOptimizedJobMatchScore: Invalid originalText');
    return Math.min(100, initialJobMatchScore + 10); // Default improvement
  }
  
  if (!optimizedText || typeof optimizedText !== 'string') {
    console.warn('calculateOptimizedJobMatchScore: Invalid optimizedText');
    return Math.min(100, initialJobMatchScore + 10); // Default improvement
  }
  
  // Calculate the new job match score for the optimized resume
  const newJobMatchScore = calculateJobMatchScore(
    optimizedText,
    jobData.skills || [],
    jobData.description || ''
  );
  
  // Ensure the optimized score is at least as good as the initial score
  // and add a realistic improvement based on optimization quality
  const improvementRatio = calculateImprovementRatio(originalText, optimizedText);
  const baseImprovement = Math.floor(initialJobMatchScore * improvementRatio);
  const optimizationBonus = 5 + (simpleHash(optimizedText) % 8);
  
  // Use the higher of the calculated job match score or the improved initial score
  const calculatedScore = Math.max(
    newJobMatchScore,
    initialJobMatchScore + baseImprovement + optimizationBonus
  );
  
  // 🔧 FIX: Realistic score bounds - no optimization is perfect (92 is realistic max)
  // Real-world ATS scores rarely exceed 92-94 even for excellent optimizations
  // Ensure minimum +5 improvement, realistic maximum of 92
  const realisticMax = 92; // Realistic ceiling for optimized resumes
  const finalScore = Math.min(realisticMax, Math.max(initialJobMatchScore + 5, calculatedScore));
  
  console.log('[Score] calculateOptimizedJobMatchScore - Score breakdown:', {
    initialJobMatchScore,
    newJobMatchScore,
    improvementRatio,
    baseImprovement,
    optimizationBonus,
    calculatedScore,
    finalScore,
    improvement: finalScore - initialJobMatchScore,
    cappedAtRealisticMax: calculatedScore > realisticMax
  });
  
  // Validate the result
  if (isNaN(finalScore)) {
    console.warn('calculateOptimizedJobMatchScore: Calculated score is NaN, returning default improvement');
    return Math.min(100, initialJobMatchScore + 10);
  }
  
  return finalScore;
}

/**
 * Calculates optimized score based on initial score and improvements (legacy function)
 * @param initialScore Initial score
 * @param originalText Original resume text
 * @param optimizedText Optimized resume text
 * @returns Optimized score (0-100)
 */
export function calculateOptimizedScore(initialScore: number, originalText: string, optimizedText: string): number {
  // Validate inputs
  if (typeof initialScore !== 'number' || isNaN(initialScore)) {
    console.warn('calculateOptimizedScore: Invalid initialScore, using default');
    initialScore = 65;
  }
  
  if (!originalText || typeof originalText !== 'string') {
    console.warn('calculateOptimizedScore: Invalid originalText');
    return Math.min(100, initialScore + 10); // Default improvement
  }
  
  if (!optimizedText || typeof optimizedText !== 'string') {
    console.warn('calculateOptimizedScore: Invalid optimizedText');
    return Math.min(100, initialScore + 10); // Default improvement
  }
  
  const improvementRatio = calculateImprovementRatio(originalText, optimizedText);
  
  // Calculate base improvement
  const baseImprovement = Math.floor(initialScore * improvementRatio);
  
  // Deterministic variation based on content hash
  const optimizationVariation = 8 + (simpleHash(optimizedText) % 12);
  
  const calculatedScore = initialScore + baseImprovement + optimizationVariation;
  
  // 🔧 FIX: Realistic score bounds - no optimization is perfect (92 is realistic max)
  // Real-world ATS scores rarely exceed 92-94 even for excellent optimizations
  // Ensure minimum +8 improvement, realistic maximum of 92
  const realisticMax = 92; // Realistic ceiling for optimized resumes
  const finalScore = Math.min(realisticMax, Math.max(initialScore + 8, calculatedScore));
  
  console.log('[Score] calculateOptimizedScore - Score breakdown:', {
    initialScore,
    improvementRatio,
    baseImprovement,
    optimizationVariation,
    calculatedScore,
    finalScore,
    improvement: finalScore - initialScore,
    cappedAtRealisticMax: calculatedScore > realisticMax
  });
  
  // Validate the result
  if (isNaN(finalScore)) {
    console.warn('calculateOptimizedScore: Calculated score is NaN, returning default improvement');
    return Math.min(100, initialScore + 10);
  }
  
  return finalScore;
}

/**
 * Calculates improvement ratio between original and optimized text
 * @param original Original resume text
 * @param optimized Optimized resume text
 * @returns Improvement ratio (0.0-0.5)
 */
export function calculateImprovementRatio(original: string, optimized: string): number {
  // Word count improvement
  const origWordCount = original.split(/\s+/).length || 1;
  const optWordCount = optimized.split(/\s+/).length;
  const wordCountRatio = Math.abs(optWordCount - origWordCount) / origWordCount;
  
  // Keyword density improvement
  const keywordImprovement = (
    (optimized.match(/\b(achieved|improved|increased|managed|led|developed)\b/gi) || []).length -
    (original.match(/\b(achieved|improved|increased|managed|led|developed)\b/gi) || []).length
  ) / 10; // Normalize to 0-0.1 range
  
  // Metrics improvement
  const metricsImprovement = (
    (optimized.match(/\d+%/g) || []).length -
    (original.match(/\d+%/g) || []).length
  ) / 5; // Normalize to 0-0.1 range
  
  // Calculate total improvement (capped at 0.5 = 50% improvement)
  return Math.min(0.5, 0.1 + wordCountRatio * 0.2 + keywordImprovement + metricsImprovement);
}

/**
 * Calculates job match score based on resume text and job requirements
 * @param resumeText Resume text
 * @param requiredSkills Required skills from job
 * @param jobDescription Job description
 * @returns Match score (0-100)
 */
/**
 * Escapes special regex characters in a string (e.g. C++ -> C\+\+)
 * @param str String to escape
 * @returns Escaped string safe for use in RegExp
 */
export function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function calculateJobMatchScore(
  resumeText: string,
  requiredSkills: string[],
  jobDescription: string
): number {
  console.log('[Score] calculateJobMatchScore - Input parameters:', {
    resumeTextLength: resumeText?.length || 0,
    resumeTextPreview: resumeText?.substring(0, 100) || '',
    requiredSkillsCount: requiredSkills?.length || 0,
    requiredSkills: requiredSkills || [],
    jobDescriptionLength: jobDescription?.length || 0,
    jobDescriptionPreview: jobDescription?.substring(0, 100) || ''
  });
  
  // Validate inputs
  if (!resumeText || typeof resumeText !== 'string') {
    console.warn('calculateJobMatchScore: Invalid resumeText');
    console.log('[Score] calculateJobMatchScore - Returning default score (75) due to invalid resumeText');
    return 75; // Default score
  }
  
  // If no skills or description, return default score
  if ((!requiredSkills || requiredSkills.length === 0) && !jobDescription) {
    console.log('[Score] calculateJobMatchScore - Returning default score (75) due to no skills or description');
    return 75; // Default score
  }
  
  // Calculate skill match percentage
  let skillMatchScore = 0;
  if (requiredSkills && requiredSkills.length > 0) {
    const matchedSkills = requiredSkills.filter(skill => {
      // Escape special regex characters (like + in C++)
      const escapedSkill = escapeRegExp(skill);
      return new RegExp(`\\b${escapedSkill}\\b`, 'i').test(resumeText);
    }).length;
    
    skillMatchScore = Math.round((matchedSkills / requiredSkills.length) * 60);
    
    console.log('[Score] calculateJobMatchScore - Skill matching analysis:', {
      totalRequiredSkills: requiredSkills.length,
      matchedSkillsCount: matchedSkills,
      matchedSkills: requiredSkills.filter(skill => {
        const escapedSkill = escapeRegExp(skill);
        return new RegExp(`\\b${escapedSkill}\\b`, 'i').test(resumeText);
      }),
      skillMatchScore: skillMatchScore,
      skillMatchPercentage: (matchedSkills / requiredSkills.length) * 100
    });
  }
  
  // Calculate keyword match score
  let keywordScore = 0;
  if (jobDescription) {
    // Extract significant keywords (4+ chars) from job description
    const keywords = (jobDescription.match(/\b[a-zA-Z]{4,}\b/g) || [])
      .filter(word => !['this', 'that', 'with', 'from', 'have', 'your'].includes(word.toLowerCase()));
    
    if (keywords.length > 0) {
      // Count matches in resume (escape special regex chars)
      const keywordMatches = keywords.filter(keyword => {
        const escapedKeyword = escapeRegExp(keyword);
        return new RegExp(`\\b${escapedKeyword}\\b`, 'i').test(resumeText);
      }).length;
      
      keywordScore = Math.round((keywordMatches / Math.min(keywords.length, 50)) * 40);
      
      console.log('[Score] calculateJobMatchScore - Keyword matching analysis:', {
        totalKeywords: keywords.length,
        keywordsUsedForScoring: Math.min(keywords.length, 50),
        matchedKeywordsCount: keywordMatches,
        matchedKeywords: keywords.filter(keyword => {
          const escapedKeyword = escapeRegExp(keyword);
          return new RegExp(`\\b${escapedKeyword}\\b`, 'i').test(resumeText);
        }).slice(0, 10), // Show first 10 matched keywords
        keywordScore: keywordScore,
        keywordMatchPercentage: (keywordMatches / Math.min(keywords.length, 50)) * 100
      });
    }
  }
  
  // Combine scores
  const totalScore = skillMatchScore + keywordScore;
  
  console.log('[Score] calculateJobMatchScore - Score combination:', {
    skillMatchScore,
    keywordScore,
    totalScore,
    beforeBounds: totalScore
  });
  
  // Ensure minimum score of 50 and maximum of 100, and validate result
  const finalScore = Math.min(100, Math.max(50, totalScore));
  
  console.log('[Score] calculateJobMatchScore - Final score calculation:', {
    totalScoreBeforeBounds: totalScore,
    finalScoreAfterBounds: finalScore,
    wasCappedat100: totalScore > 100,
    wasRaisedTo50: totalScore < 50
  });
  
  // Validate the result
  if (isNaN(finalScore)) {
    console.warn('calculateJobMatchScore: Calculated score is NaN, returning default');
    console.log('[Score] calculateJobMatchScore - Returning default score (75) due to NaN result');
    return 75;
  }
  
  console.log('[Score] calculateJobMatchScore - FINAL RESULT:', finalScore);
  return finalScore;
}
